import { memo, useEffect, useRef } from "react";

const FRAME_INTERVAL_MS = 5000;
const FADE_MS = 3500; // keep in sync with the canvas opacity transition in styles.scss

// Runtime performance guard tuning: judge 4s windows while the video plays;
// two consecutive windows under 24fps → the glow is hurting the experience.
// A device that stays healthy for 8 windows is proven, and sampling stops.
const SAMPLE_WINDOW_MS = 4000;
const MIN_HEALTHY_FPS = 24;
const BAD_WINDOWS_TO_DEGRADE = 2;
const GOOD_WINDOWS_TO_TRUST = 8;

/**
 * AmbientGlow — "iluminação cinematográfica": a blurred copy of the current
 * frame glowing behind the player, refreshed every few seconds while playing.
 *
 * Base + overlay crossfade (no "off/on" dip): the base canvas holds the
 * current frame at full opacity; the overlay draws the next frame and fades
 * in ON TOP of it, so the old glow is only ever covered, never dimmed. Once
 * the fade completes the overlay is baked into the base and reset invisibly.
 * No React state — a re-render per cycle would be waste.
 *
 * While mounted, an rAF sampler watches the page frame rate; sustained low
 * FPS during playback calls `onPerformanceIssue` so the owner can turn the
 * effect off (this component only exists while the effect is enabled).
 */
function AmbientGlow({ videoRef, onPerformanceIssue }) {
    const baseRef = useRef(null);
    const overlayRef = useRef(null);

    useEffect(() => {
        const video = videoRef.current;
        const base = baseRef.current;
        const overlay = overlayRef.current;
        if (!video || !base || !overlay) return;

        let hasBase = false;
        let overlayActive = false;
        let bakeTimer = null;

        const drawFrame = (canvas) => {
            if (video.readyState < 2) return false;
            try {
                canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
                return true;
            } catch {
                return false; // tainted canvas / decode hiccup — keep the last glow
            }
        };

        // Copy the fully-faded overlay into the base and reset the overlay's
        // opacity without animating (transition suspended around the reset).
        const bake = () => {
            base.getContext("2d").drawImage(overlay, 0, 0);
            overlay.style.transition = "none";
            overlay.style.opacity = 0;
            void overlay.offsetWidth; // flush so the restored transition skips the reset
            overlay.style.transition = "";
            overlayActive = false;
        };

        const cycle = () => {
            // Seed the base instantly on the first decodable frame (the
            // wrapper's own fade brings it in); crossfades only while playing.
            if (!hasBase) {
                if (drawFrame(base)) {
                    base.style.opacity = 1;
                    hasBase = true;
                }
                return;
            }

            if (video.paused || video.ended) return;

            // A fade is still in flight (play/pause toggles can re-trigger
            // cycles) — snapping it forward reads as a hard cut, so let it
            // finish; the next interval tick will bring the newer frame.
            if (overlayActive) return;

            if (!drawFrame(overlay)) return;

            overlayActive = true;
            overlay.style.opacity = 1; // CSS transition performs the fade-in
            bakeTimer = setTimeout(bake, FADE_MS + 100);
        };

        cycle();
        const interval = setInterval(cycle, FRAME_INTERVAL_MS);

        video.addEventListener("loadeddata", cycle);
        video.addEventListener("play", cycle);

        return () => {
            clearInterval(interval);
            if (bakeTimer) clearTimeout(bakeTimer);
            video.removeEventListener("loadeddata", cycle);
            video.removeEventListener("play", cycle);
        };
    }, [videoRef]);

    // ── Runtime performance guard ────────────────────────────────────────
    const onPerformanceIssueRef = useRef(onPerformanceIssue);
    onPerformanceIssueRef.current = onPerformanceIssue;

    useEffect(() => {
        let rafId = null;
        let frames = 0;
        let windowStart = performance.now();
        let badWindows = 0;
        let goodWindows = 0;

        const tick = (now) => {
            frames += 1;
            const elapsed = now - windowStart;

            if (elapsed >= SAMPLE_WINDOW_MS) {
                const video = videoRef.current;
                // Judge only clean windows: video actually playing, tab
                // visible, and no rAF starvation gap (hidden tab, debugger).
                const judgeable =
                    elapsed < SAMPLE_WINDOW_MS * 2
                    && video && !video.paused && !video.ended
                    && document.visibilityState === "visible";

                if (judgeable) {
                    const fps = (frames * 1000) / elapsed;
                    if (fps < MIN_HEALTHY_FPS) {
                        badWindows += 1;
                        goodWindows = 0;
                        if (badWindows >= BAD_WINDOWS_TO_DEGRADE) {
                            onPerformanceIssueRef.current?.();
                            return; // stop sampling — the owner unmounts us
                        }
                    } else {
                        badWindows = 0;
                        goodWindows += 1;
                        if (goodWindows >= GOOD_WINDOWS_TO_TRUST) {
                            return; // device proven healthy — stop sampling
                        }
                    }
                }

                frames = 0;
                windowStart = now;
            }

            rafId = requestAnimationFrame(tick);
        };

        rafId = requestAnimationFrame(tick);
        return () => {
            if (rafId !== null) cancelAnimationFrame(rafId);
        };
    }, [videoRef]);

    return (
        <div className="player-ambient" aria-hidden="true">
            <canvas ref={baseRef} width={64} height={36} />
            <canvas ref={overlayRef} width={64} height={36} />
        </div>
    );
}

export default memo(AmbientGlow);
